import { useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Field,
  FieldHelp,
  FieldLabel,
  Input
} from '@axilon-apps/libs-ui-shadcn'
import { AlertTriangle } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import {
  CopyToClipboard,
  FlashMessages,
  useFlashMessages,
  useFormValidationErrors
} from '@axilon-apps/libs-ui-components'

import { AddUserFormData } from '../../../../../../types/add-user-form'
import addUserValidationSchema from '../../../../../../validations/add-user-validation-schema'
import { UsersFlashMessageGroup } from '../../../../../../constants/users-flash-message-groups'
import type { FieldErrors } from '@axilon-apps/domains-identity/types'

interface Props {
  onSubmit: (data: AddUserFormData) => void
  onCancel: () => void
  password: string | null
  fieldErros?: FieldErrors<AddUserFormData>
  showEmail: boolean
}

const AddUserForm = ({ onSubmit, onCancel, password, fieldErros, showEmail }: Props) => {
  const [passwordCopied, setPasswordCopied] = useState(false)

  const instruction = `Password needs to be copied before closing.
    System generated password is provided only this one time.`

  const cancel = () => {
    reset(defaultValues)
    onCancel()
  }

  const defaultValues = { name: '', username: '', email: '' } satisfies AddUserFormData

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm({
    defaultValues,
    resolver: zodResolver(addUserValidationSchema)
  })

  const flash = useFlashMessages(UsersFlashMessageGroup.AddUser)
  const onValidationError = useFormValidationErrors(flash)

  return (
    <form onSubmit={handleSubmit(onSubmit, onValidationError)} noValidate>
      <div className="flex flex-col gap-6">
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Field data-invalid={Boolean(errors.name || fieldErros?.name) || undefined}>
              <FieldLabel required>Name</FieldLabel>
              <Input {...field} type="text" />
              <FieldHelp>{errors.name?.message || fieldErros?.name}</FieldHelp>
            </Field>
          )}
        />
        <Controller
          control={control}
          name="username"
          render={({ field }) => (
            <Field data-invalid={Boolean(errors.username || fieldErros?.username) || undefined}>
              <FieldLabel required>Username</FieldLabel>
              <Input {...field} type="text" />
              <FieldHelp>{errors.username?.message || fieldErros?.username}</FieldHelp>
            </Field>
          )}
        />
        {showEmail && (
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.email || fieldErros?.email) || undefined}>
                <FieldLabel>Email</FieldLabel>
                <Input {...field} type="email" />
                <FieldHelp>{errors.email?.message || fieldErros?.email}</FieldHelp>
              </Field>
            )}
          />
        )}
        {password && (
          <>
            <Field>
              <FieldLabel>Password</FieldLabel>
              <div className="relative">
                <Input readOnly name="password" value={password} className="pr-10" />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <CopyToClipboard textToCopy={password} onCopySuccess={setPasswordCopied} />
                </div>
              </div>
            </Field>

            <Alert className="border-st-warn/40">
              <AlertTriangle className="size-4 text-st-warn" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>{instruction}</AlertDescription>
            </Alert>
          </>
        )}
        <FlashMessages group={UsersFlashMessageGroup.AddUser} />
        {!password && (
          <div className="flex flex-row justify-end gap-4">
            <Button type="button" variant="secondary" onClick={cancel}>
              Cancel
            </Button>
            <Button type="submit">Add user</Button>
          </div>
        )}
        {password && (
          <Button type="button" disabled={!passwordCopied} onClick={cancel}>
            Close
          </Button>
        )}
      </div>
    </form>
  )
}

export default AddUserForm
